import { CuriosityNamespaceType, CuriosityTranslation, CuriosityValue } from '@procureai/curiosity-ui-mui';
import { Permission } from '@procureai/permissions';
import { identity, isNotNil } from 'ramda';

import { BaseLink } from '../BaseLink/BaseLink';
import { NULLISH_VALUE } from '~/common/constants';
import { RouteId } from '~/routes';

export interface CuriosityTranslationRenderResult {
  displayValue: React.ReactNode;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
}
export interface CuriosityBaseLinkProps {
  ns: CuriosityBaseNamespace;
  i18nKey: string;
  render?: (curiosityTranslationValue: CuriosityTranslation) => CuriosityTranslationRenderResult;
  fallback?: string;
}

type CuriosityBaseNamespace =
  | CuriosityNamespaceType.COMMODITY
  | CuriosityNamespaceType.COMMODITY_GROUP
  | CuriosityNamespaceType.CONTRACT
  | CuriosityNamespaceType.CONTRACT_ITEM
  | CuriosityNamespaceType.SUPPLIER;

const namespacePermissionMap: { [Key in CuriosityBaseNamespace]: Permission } = {
  [CuriosityNamespaceType.COMMODITY]: Permission.BaseCommodityList,
  [CuriosityNamespaceType.COMMODITY_GROUP]: Permission.BaseCommodityGroupList,
  [CuriosityNamespaceType.CONTRACT]: Permission.BaseContractList,
  [CuriosityNamespaceType.CONTRACT_ITEM]: Permission.BaseContractList,
  [CuriosityNamespaceType.SUPPLIER]: Permission.BaseSupplierList,
};

type RoutingFuncton = (translation: CuriosityTranslation) => string;
const namespaceRouteMap: { [Key in CuriosityBaseNamespace]: RoutingFuncton } = {
  [CuriosityNamespaceType.COMMODITY]: (translation) => RouteId.commodityDetail(translation.key),
  [CuriosityNamespaceType.COMMODITY_GROUP]: (translation) => RouteId.commodityGroupDetail(translation.key),
  [CuriosityNamespaceType.CONTRACT]: (translation) => RouteId.contractDetail(translation.key),
  [CuriosityNamespaceType.CONTRACT_ITEM]: (translation) => RouteId.contractDetail(translation.entity.contract.id),
  [CuriosityNamespaceType.SUPPLIER]: (translation) => RouteId.supplierDetail(translation.id),
};

export const CuriosityBaseLink = ({ ns, i18nKey, fallback = NULLISH_VALUE, render }: CuriosityBaseLinkProps) => {
  return (
    <CuriosityValue ns={ns} i18nKey={i18nKey} fallback={fallback} valueGetter={identity}>
      {(curiosityResult) => {
        if (curiosityResult === fallback) {
          return <>{fallback}</>;
        }
        const curiosityTranslationValue = curiosityResult as CuriosityTranslation;

        const { prefix, displayValue, suffix } = isNotNil(render)
          ? render(curiosityTranslationValue)
          : { displayValue: curiosityTranslationValue.value };

        return (
          <>
            {prefix}
            <BaseLink
              link={{
                needs: {
                  permission: namespacePermissionMap[ns],
                },
                to: namespaceRouteMap[ns](curiosityTranslationValue),
              }}
            >
              {displayValue}
            </BaseLink>
            {suffix}
          </>
        );
      }}
    </CuriosityValue>
  );
};
